import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

const ON_DUP_OPTIONS = [
  { value: 'replace_archive', label: 'Replace (archive old)', help: 'Old record(s) are marked as archived; the new record is inserted as the active one.' },
  { value: 'update_in_place', label: 'Update in place', help: 'The most recent existing record is overwritten with the new fields (same ID/created_at).' },
  { value: 'refuse', label: 'Refuse', help: 'New apply attempts fail with an error when the cap is reached.' },
];

const STATUS_OPTIONS = [
  { value: 'published', label: 'Published — visible publicly' },
  { value: 'review', label: 'In review — internal only' },
  { value: 'draft', label: 'Draft — work-in-progress' },
];

const TARGET_LABELS = {
  supervisor_of_elections: 'Supervisor of Elections',
  election_dates: 'Election Dates',
};

export default function CivicRulesPanel({ stateId, stateName }) {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});  // local edits keyed by target_type

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.get(`/admin/states/${stateId}/civic-rules`);
      setRules(res.data || []);
      const init = {};
      (res.data || []).forEach((r) => { init[r.target_type] = { ...r }; });
      setDrafts(init);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stateId]);

  function patchDraft(targetType, field, value) {
    setDrafts((d) => ({ ...d, [targetType]: { ...d[targetType], [field]: value } }));
  }

  async function save(targetType) {
    const draft = drafts[targetType];
    if (!draft) return;
    setSavingKey(targetType);
    try {
      await adminApi.put(`/admin/states/${stateId}/civic-rules/${targetType}`, {
        label: draft.label,
        max_per_county: parseInt(draft.max_per_county, 10) || 0,
        on_duplicate: draft.on_duplicate,
        apply_status: draft.apply_status,
      });
      toast.success(`Rule saved`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingKey(''); }
  }

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center"><Spinner animation="border" size="sm" /></Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-3" data-testid="civic-rules-panel">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <i className="bi bi-shield-check me-2" />
          <span className="fw-semibold">Civic data rules</span>
          <Badge bg="light" text="dark" className="ms-2">{rules.length} rule{rules.length === 1 ? '' : 's'}</Badge>
        </div>
        <div className="text-muted" style={{ fontSize: '0.82rem' }}>
          Controls how AI-imported records are saved for {stateName || 'this state'}.
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {rules.map((r) => {
          const draft = drafts[r.target_type] || r;
          const dupHelp = ON_DUP_OPTIONS.find((o) => o.value === draft.on_duplicate)?.help;
          return (
            <div key={r.target_type} className="pb-3 mb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }} data-testid={`civic-rule-row-${r.target_type}`}>
              <Row className="g-3 align-items-end">
                <Col xs={12} md={3}>
                  <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Civic data type</Form.Label>
                  <div>
                    <Badge bg="secondary" className="me-2">{TARGET_LABELS[r.target_type] || r.target_type}</Badge>
                  </div>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Label in this state</Form.Label>
                    <Form.Control
                      value={draft.label || ''}
                      onChange={(e) => patchDraft(r.target_type, 'label', e.target.value)}
                      placeholder="e.g., County Elections Administrator"
                      data-testid={`civic-rule-${r.target_type}-label-input`}
                    />
                    <Form.Text>How locals refer to this role.</Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Max per county</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={50}
                      value={draft.max_per_county ?? 1}
                      onChange={(e) => patchDraft(r.target_type, 'max_per_county', e.target.value)}
                      data-testid={`civic-rule-${r.target_type}-max-input`}
                    />
                    <Form.Text>0 = unlimited</Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>On duplicate</Form.Label>
                    <Form.Select
                      value={draft.on_duplicate}
                      onChange={(e) => patchDraft(r.target_type, 'on_duplicate', e.target.value)}
                      data-testid={`civic-rule-${r.target_type}-on-duplicate-select`}
                    >
                      {ON_DUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Apply status</Form.Label>
                    <Form.Select
                      value={draft.apply_status}
                      onChange={(e) => patchDraft(r.target_type, 'apply_status', e.target.value)}
                      data-testid={`civic-rule-${r.target_type}-apply-status-select`}
                    >
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col xs={12}>
                  {dupHelp && <div className="text-muted" style={{ fontSize: '0.8rem' }}><i className="bi bi-info-circle me-1" />{dupHelp}</div>}
                </Col>
              </Row>
              <Row className="mt-2">
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => save(r.target_type)}
                    disabled={savingKey === r.target_type}
                    data-testid={`civic-rule-${r.target_type}-save-button`}
                  >
                    {savingKey === r.target_type ? <Spinner size="sm" animation="border" /> : 'Save rule'}
                  </Button>
                </Col>
              </Row>
            </div>
          );
        })}
        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
          <i className="bi bi-lightbulb me-1" />
          These rules apply automatically the next time you click <strong>Apply approved</strong> on an Import Job for this state.
        </div>
      </Card.Body>
    </Card>
  );
}
